import { Button, Card, List, ListItem, ListItemIcon, ListItemText } from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoQuizAttempt } from "@/shared/model/edo-quiz-attempt.model";
import { IEdoQuizQuestion } from "@/shared/model/edo-quiz-question.model";
import { getEntity as getQuizAttempt } from "@/shared/reducers/entities/edo-quiz-attempt.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { getQuestionIcon } from "@/shared/util/question-utils";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Translate } from "react-jhipster";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import CourseQuestion from "./components/CourseQuestion";

const QuizAttemptRoute = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams<{ id: string }>();
    const { entity: quizAttempt } = useAppSelector<EntityState<IEdoQuizAttempt>>(state => state.edoQuizAttempt);
    const [questions, setQuestions] = useState<IEdoQuizQuestion[]>([]);
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const interval = useRef<ReturnType<typeof setInterval> | null>(null);

    const getQuizQuestions = async () => {
        const response = await axios.get<{ data: IEdoQuizQuestion[] }>(
            `/quiz-attempts/${params.id}/questions`,
            { params: { sort: "updatedAt,ASC" } }
        );
        setQuestions(response.data.data);
    };

    useEffect(() => {
        dispatch(getQuizAttempt(params.id));
        return () => {
            if (interval.current) clearInterval(interval.current);
        };
    }, [dispatch, params.id]);

    const handleTerminateAttempt = async () => {
        await axios.patch(`/quiz-attempts/${quizAttempt.id}/end`);
        navigate(
            `/courses/${quizAttempt?.courseQuiz?.course?.id}/quizzes/${quizAttempt?.courseQuiz?.id}/start`
        );
    };

    useEffect(() => {
        if (quizAttempt?.id) getQuizQuestions();
        if (quizAttempt?.timeFinish) {
            interval.current = setInterval(() => {
                const timeFinish = new Date(quizAttempt.timeFinish).getTime();
                const timeLeftLocal = (timeFinish - Date.now()) / 1000;
                setTimeLeft(timeLeftLocal);
                if (timeLeftLocal <= 0) handleTerminateAttempt();
            }, 1000);
        }
    }, [quizAttempt?.id]);

    if (
        quizAttempt?.timeFinish != null &&
        quizAttempt?.courseQuiz?.course?.id &&
        new Date(quizAttempt.timeFinish).getTime() < Date.now()
    ) {
        return (
            <Navigate
                replace
                to={`/courses/${quizAttempt.courseQuiz.course.id}/quizzes/${quizAttempt.courseQuiz.id}/start`}
            />
        );
    }

    return (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="space-y-5 lg:col-span-8">
                <Card className="surface p-6">
                    <h2 className="mb-2 text-heading-2 font-semibold tracking-tight text-[var(--color-mockup-text)]">
                        {quizAttempt?.courseQuiz?.quiz?.title}
                    </h2>
                    <p className="text-body text-[var(--color-mockup-text-mid)]">
                        {quizAttempt?.courseQuiz?.quiz?.description}
                    </p>
                </Card>
                <div className="space-y-5">
                    {questions.length ? (
                        questions.map((question) => (
                            <CourseQuestion
                                key={question.id}
                                className="surface surface-hover"
                                quizQuestion={question}
                                quizAttempt={quizAttempt}
                            />
                        ))
                    ) : (
                        <Card className="surface p-6">
                            <p className="text-center text-body text-[var(--color-mockup-text-mid)]">
                                <Translate contentKey="edocendoApp.edoQuestion.home.notFound">
                                    No Edo Questions found
                                </Translate>
                            </p>
                        </Card>
                    )}
                </div>
            </div>
            <div className="space-y-5 lg:col-span-4">
                <Card className="surface overflow-hidden">
                    <div className="border-b border-[var(--color-mockup-border)] px-5 py-3 dark:border-[var(--color-mockup-border)]">
                        <h3 className="text-body font-medium text-[var(--color-mockup-text)]">Informazioni</h3>
                    </div>
                    <div className="p-5">
                        <p className="mb-1 text-body font-medium text-[var(--color-mockup-text)]">
                            Tempo rimasto
                        </p>
                        <p className="text-body-sm text-[var(--color-mockup-text-mid)]">
                            {Math.trunc(timeLeft / 3600)} ore, {Math.trunc((timeLeft / 60) % 60)} minuti,{" "}
                            {Math.trunc(timeLeft % 60)} secondi
                        </p>
                        <Button
                            variant="primary"
                            className="mt-4 w-full transition-all duration-200 hover:scale-[1.02]"
                            onClick={() => handleTerminateAttempt()}
                        >
                            Consegna
                        </Button>
                    </div>
                </Card>
                <Card className="surface overflow-hidden">
                    <div className="border-b border-[var(--color-mockup-border)] px-5 py-3 dark:border-[var(--color-mockup-border)]">
                        <h3 className="text-body font-medium text-[var(--color-mockup-text)]">Domande</h3>
                    </div>
                    <List className="divide-y divide-[var(--color-mockup-border)]">
                        {questions.map((quizQuestion, index) => (
                            <ListItem
                                key={quizQuestion.id}
                                className="flex cursor-default items-center gap-3 px-5 py-3"
                            >
                                <ListItemIcon className="shrink-0">
                                    {getQuestionIcon(quizQuestion.question.type)}
                                </ListItemIcon>
                                <ListItemText primary={`Domanda ${index + 1}`} />
                            </ListItem>
                        ))}
                    </List>
                </Card>
            </div>
        </div>
    );
};

export default QuizAttemptRoute;
